/**
 * What SellGPT reports to Google Analytics, and - more importantly - what it does not.
 *
 * WHY A PAGE VIEW HAS TO BE SENT BY HAND
 *   gtag records one page view when it loads. On a single page application that is the only one it would
 *   ever record: every later screen is a route change with no page load behind it, so "My sales" and a
 *   tracked sale would not exist in the reports at all. index.html therefore configures the tag with
 *   send_page_view:false and this module sends one per route, the first included.
 *
 * WHAT IS REMOVED BEFORE ANYTHING IS SENT
 *   A tracked sale lives at /sale/SGP20260924171239955. That lead number is a customer's own reference -
 *   it is on their paperwork and they quote it to support - and handing a stream of them to an analytics
 *   vendor would be sending customer data to a third party for no analytical gain: nobody needs to know
 *   which individual sale was viewed, only that a sale was viewed. So the path is rewritten to
 *   /sale/:leadNumber, and any query string is dropped entirely rather than trusted to be harmless.
 *
 *   Nothing else is ever sent. There is no event carrying a mobile number, an OTP, an address, a pincode,
 *   a device the customer is selling or a price they were quoted.
 */

type Gtag = (command: string, ...args: unknown[]) => void

declare global {
  interface Window {
    gtag?: Gtag
    __GA_ID__?: string
  }
}

/** True only when index.html actually loaded the tag - i.e. a real measurement ID was built in. */
export const analyticsEnabled = (): boolean =>
  typeof window !== 'undefined' && typeof window.gtag === 'function' && Boolean(window.__GA_ID__)

/**
 * The reportable form of a path: identifiers replaced with their placeholder, query and hash dropped.
 *
 * Exported for its own sake - this is the rule worth testing, and testing it through the router would
 * test react-router rather than the rule.
 */
export function reportablePath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0]

  // /sale/<lead number> is the only route carrying an identifier today. Written as a general rule rather
  // than a string replace so that a second segment never slips through unnoticed.
  const sale = /^\/sale\/[^/]+/.exec(path)
  if (sale) return '/sale/:leadNumber'

  return path === '' ? '/' : path
}

/** One page view for a route the customer has navigated to. Silent when analytics are not configured. */
export function trackPageView(pathname: string): void {
  if (!analyticsEnabled()) return

  window.gtag?.('event', 'page_view', {
    page_path: reportablePath(pathname),
    // Sent explicitly so the recorded location can never include a query string that a future link,
    // campaign tag or redirect happens to add.
    page_location: window.location.origin + reportablePath(pathname),
    page_title: document.title,
  })
}
